// [목적] /folio/:id/edit 편집 모드 — Day별 PlanItem 드래그앤드롭 재배열 + 고정/삭제/직접 추가 + Day 추가/삭제
//        + AI 자연어 보정(활성 Day 기준)
// [이유] folio-crud reorder API 의 첫 진짜 소비자. design_structure §2-4 IA + ADR-6
//        FlowLine A9(f0c27bb2): 항목 고정(🔒)/삭제/직접 추가, Day 추가/삭제(useDayPlans), isError/404 분기
//        (무한 "로딩 중" 제거), 빈 Day 안내를 추가해 편집 화면을 실사용 가능한 수준으로 확장.
//        FlowLine dcc2c3cc FE 과업1: 활성 Day 탭 하단에 RefineInputBar + RefineProposalDialog 부착 —
//        scope={type:'day', dayIndex: day.dayIndex}(현재 활성 탭)로 useFolioRefine.propose() 호출.
// [사이드 이팩트] 마우스/터치 + 키보드 모두 reorder 가능 (KeyboardSensor). 낙관적 업데이트 + 실패 시 롤백.
//                고정(locked)된 항목은 드래그 핸들이 비활성화됨(위치 보호 — 다만 다른 항목이 그 옆으로
//                재배치되는 것까지 막지는 않음, 완전한 위치 고정은 후속 과제).
//                POI 검색 API(GET /pois 검색)가 BE 에 없어 "직접 추가"는 이름 직접 입력만 지원.
//                useFolioRefine 적용 성공 시 queryKeys.folio(folioId) invalidate — day.items 가 자동 갱신됨.
// [수정 시 고려사항] PoiInfoPanel은 placeholder — 후속 poi-detail Spec에서 GET /pois/:id 연결.
//                   isError 404 분기는 useFolio 의 ApiError.status 를 그대로 참조(FolioPage.tsx StateMessage 와 동일 관례).
//                   AI 보정은 day 가 존재할 때만 노출(days.length===0 이면 렌더하지 않음).

import {
  Box,
  Button,
  ButtonBase,
  IconButton,
  InputBase,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { tokens } from '@gangwoncanvas/ui';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useEffect, useState } from 'react';
import { Link as RouterLink, useBlocker, useParams } from 'react-router-dom';
import type { PlanItem, UpdateItemInput } from '@gangwoncanvas/types';

/** planItemSchema.type(=kind) 유니온 — packages/types 가 별도 이름을 export 하지 않아 PlanItem 에서 파생 */
type PlanItemKind = PlanItem['type'];

import { PageContainer } from '../components/layout/PageContainer.js';
import { useToast } from '../components/feedback/Toast.js';
import { ApiError } from '../api/http.js';
import { useFolio } from '../hooks/useFolio.js';
import { useReorderItems } from '../hooks/useReorderItems.js';
import { useCreateDay, useDeleteDay } from '../hooks/useDayPlans.js';
import { useCreateItem, useDeleteItem, useUpdateItem } from '../hooks/usePlanItems.js';
import { useFolioRefine } from '../hooks/useFolioRefine.js';
import { RefineInputBar } from '../components/folio/refine/RefineInputBar.js';
import { RefineProposalDialog } from '../components/folio/refine/RefineProposalDialog.js';
import { LineIcon, type LineIconName } from '../components/icons/LineIcons.js';

import { getItemEmoji } from './FolioPage.js';

// ─── 디자인 토큰 ───────────────────────────────────────────────────────────────
const c = tokens.color;
const L = 'light' as const;
const INK_HI = c.ink.high[L];
const INK_MID = c.ink.mid[L];
const INK_LOW = c.ink.low[L];
const LINE = c.line.base[L];
const PAPER0 = c.paper[0][L];
const PAPER1 = c.paper[1][L];
const PAPER2 = c.paper[2][L];
const BRAND = c.brand[500][L];
const BRAND_TEXT = c.brand[600][L];
const DANGER = c.danger[500][L];
const MONO = 'JetBrains Mono, ui-monospace, monospace';

/**
 * [목적] planItemKindEnum → 한글 라벨 단일 소스
 * [이유] 2026-09-21 디자인 QA: 항목 행이 BE enum 원문(place/meal/activity/note)을 그대로 노출하고
 *        있었다 — 사용자에게 영문 enum 을 보이지 않도록 표시명을 분리한다.
 * [사이드 이팩트] 직접 추가 폼의 선택지(ITEM_KIND_OPTIONS)도 이 맵에서 파생 — 두 곳이 갈라질 수 없다.
 * [수정 시 고려사항] planItemKindEnum 에 kind 추가 시 본 맵과 FolioPage.PLAN_ITEM_EMOJI_MAP 동시 갱신.
 */
const ITEM_KIND_LABEL: Record<PlanItemKind, string> = {
  place: '장소',
  meal: '식사',
  stay: '숙소',
  transit: '이동',
  activity: '액티비티',
  note: '메모',
};

/** planItemKindEnum(packages/types) 과 1:1 대응 — 직접 추가 폼의 유형 선택지 */
const ITEM_KIND_OPTIONS: ReadonlyArray<{ value: PlanItemKind; label: string }> = (
  Object.keys(ITEM_KIND_LABEL) as PlanItemKind[]
).map((value) => ({ value, label: ITEM_KIND_LABEL[value] }));

/**
 * [목적] PlanItem.startAt/endAt(ISO) → "HH:MM" 또는 "HH:MM–HH:MM" 표시 문자열
 * [이유] 편집 화면 항목 행이 제목만 노출해 "텐 빈" 인상을 줌 — 이미 영속된 시간을 보여준다.
 * [사이드 이팩트] 없음(순수 함수). 파싱 불가/미입력은 null 반환 → 호출부가 칩을 숨긴다.
 * [수정 시 고려사항] 브라우저 로컬 타임존 기준 — 서버 저장값은 UTC ISO 이므로 KST 사용자에게는 +9h 로 보인다.
 */
export function formatItemTimeRange(startAt: string | null, endAt: string | null): string | null {
  const hhmm = (iso: string | null): string | null => {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };
  const s = hhmm(startAt);
  const e = hhmm(endAt);
  if (s && e) return `${s}–${e}`;
  return s;
}

/**
 * [목적] 비용(정수 원) → "12,000원" 표시 문자열
 * [이유] 항목 행·Day 요약에서 동일 포맷을 쓰기 위함
 * [사이드 이팩트] 없음(순수 함수)
 * [수정 시 고려사항] 0 은 "비용 없음" 의미로 호출부가 빈 문자열 처리 — 본 함수는 0 도 그대로 포맷한다.
 */
export function formatItemCost(cost: number): string {
  return `${cost.toLocaleString('ko-KR')}원`;
}

interface InlineEditProps {
  value: string;
  placeholder: string;
  ariaLabel: string;
  onCommit: (next: string) => void;
  /** 제목처럼 강조된 본문인지(true) · 메모·비용처럼 보조 캐프션인지(false) */
  strong?: boolean;
  /** 숫자 전용 입력(비용) */
  numeric?: boolean;
  /** 읽기 모드 앞에 붙일 라인 아이콘 */
  icon?: LineIconName;
  disabled?: boolean;
}

/**
 * [목적] 클릭 → 그 자리에서 수정되는 인라인 텍스트 필드(제목·메모·비용 공용)
 * [이유] 2026-09-21 디자인 QA: 기존 편집 화면은 항목 내용을 고칠 수가 없어 "삭제 후 재추가" 가 유일한
 *        수정 수단이었다. PATCH /folios/:id/items/:itemId 는 이미 title/note/costEstimate 부분 수정을 지원한다.
 * [사이드 이팩트] Enter 또는 blur 에서만 onCommit 호출(값이 실제로 바뀜을 때만) — 타이핑 중에는 요청하지 않는다.
 *                 Escape 는 수정 취소(원래 값 복원).
 * [수정 시 고려사항] 고정(locked) 항목은 disabled 로 넣어 편집을 막는다 — 삭제/재배열 정책과 동일 기준.
 */
function InlineEdit({
  value,
  placeholder,
  ariaLabel,
  onCommit,
  strong = false,
  numeric = false,
  icon,
  disabled = false,
}: InlineEditProps): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  function commit(): void {
    setEditing(false);
    const next = draft.trim();
    if (next !== value) onCommit(next);
  }

  if (editing) {
    return (
      <InputBase
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit();
          } else if (e.key === 'Escape') {
            setDraft(value);
            setEditing(false);
          }
        }}
        inputProps={{
          'aria-label': ariaLabel,
          maxLength: numeric ? 9 : 200,
          inputMode: numeric ? 'numeric' : 'text',
        }}
        sx={{
          flex: 1,
          minWidth: 0,
          fontSize: strong ? 14 : 12,
          fontWeight: strong ? 600 : 500,
          color: INK_HI,
          bgcolor: PAPER0,
          border: `1px solid ${BRAND}`,
          borderRadius: `${tokens.radius.s}px`,
          px: '6px',
          py: 0,
        }}
      />
    );
  }

  return (
    <ButtonBase
      onClick={() => setEditing(true)}
      disabled={disabled}
      aria-label={ariaLabel}
      sx={{
        justifyContent: 'flex-start',
        gap: '4px',
        maxWidth: '100%',
        minWidth: 0,
        px: '5px',
        mx: '-5px',
        py: '1px',
        borderRadius: `${tokens.radius.s}px`,
        fontSize: strong ? 14 : 12,
        fontWeight: strong ? 600 : 500,
        letterSpacing: strong ? '-0.01em' : 0,
        color: value ? (strong ? INK_HI : INK_LOW) : INK_LOW,
        opacity: value ? 1 : 0.8,
        '&:hover': { bgcolor: disabled ? 'transparent' : PAPER2 },
        '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 1 },
      }}
    >
      {icon && <LineIcon name={icon} size={12} />}
      <Box
        component="span"
        sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
      >
        {value || placeholder}
      </Box>
    </ButtonBase>
  );
}

interface SortableRowProps {
  item: PlanItem;
  onToggleLock: () => void;
  onDelete: () => void;
  onPatch: (input: UpdateItemInput) => void;
}

/**
 * [목적] Day 편집 목록의 항목 1행 — 드래그 핸들 + 종류 칩 + 인라인 편집(제목·메모·비용) + 고정/삭제
 * [이유] FlowLine A9: 고정된 항목은 드래그·편집·삭제 불가(위치·내용 보호).
 *        2026-09-21 디자인 QA: 80px 석 줄 카드 + 영문 enum + 이모지 버튼 구성을 컴팩트 행으로 교체하고,
 *        시간·비용 메타와 인라인 편집을 추가해 "보기만 하고 고칠 수는 없는" 행을 없앤다.
 * [사이드 이팩트] useSortable({ disabled: locked }) — 고정 시 이 항목 자체는 드래그 소스가 될 수 없음.
 *                 onPatch 는 값이 실제로 바뀜 때만 호출된다(InlineEdit 책임).
 * [수정 시 고려사항] aria-label 문자열은 FolioEditPage.test.tsx 가 참조한다 — 변경 시 테스트 동시 갱신.
 *                   시간(startAt/endAt) 편집 UI 는 미제공 — 표시 전용(날짜+시간 피커 도입은 후속 과제).
 */
function SortableRow({ item, onToggleLock, onDelete, onPatch }: SortableRowProps): JSX.Element {
  const locked = item.locked;
  const title = item.title ?? '';
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
    disabled: locked,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  const timeRange = formatItemTimeRange(item.startAt, item.endAt);
  const labelBase = title || ITEM_KIND_LABEL[item.type];

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '8px',
        px: '10px',
        py: '9px',
        bgcolor: PAPER0,
        border: `1px solid ${locked ? BRAND : LINE}`,
        borderRadius: `${tokens.radius.m}px`,
        transition: `border-color ${tokens.motion.fast.duration}ms ${tokens.motion.fast.easing}`,
        '&:hover': { borderColor: locked ? BRAND : INK_LOW },
        '&:hover .ax-row-actions, &:focus-within .ax-row-actions': { opacity: 1 },
      }}
    >
      <IconButton
        {...attributes}
        {...listeners}
        disabled={locked}
        aria-label={
          locked
            ? `${labelBase} — 고정된 항목(재배열 불가)`
            : `${labelBase} 항목 재배열 핸들 (Space로 픽업, 화살표로 이동, Space로 놓기)`
        }
        sx={{
          width: 26,
          height: 26,
          mt: '1px',
          color: INK_LOW,
          opacity: locked ? 0.35 : 0.7,
          cursor: locked ? 'not-allowed' : 'grab',
          '&:active': { cursor: 'grabbing' },
          '&:hover': { bgcolor: PAPER2, opacity: 1 },
        }}
      >
        <LineIcon name="grip" size={15} />
      </IconButton>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
          <Box
            component="span"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              flexShrink: 0,
              px: '7px',
              py: '2px',
              borderRadius: 99,
              bgcolor: PAPER2,
              color: INK_MID,
              fontSize: 11,
              fontWeight: 600,
            }}
          >
            <Box component="span" aria-hidden="true">
              {getItemEmoji(item)}
            </Box>
            {ITEM_KIND_LABEL[item.type]}
          </Box>
          <InlineEdit
            value={title}
            placeholder="(제목 없음)"
            ariaLabel={`${labelBase} 제목 수정`}
            strong
            disabled={locked}
            onCommit={(next) => onPatch({ title: next || null })}
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: { xs: '6px', sm: '12px' },
            mt: '3px',
            pl: { sm: '2px' },
          }}
        >
          {timeRange && (
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: 11.5,
                fontFamily: MONO,
                color: INK_LOW,
              }}
            >
              <LineIcon name="clock" size={12} />
              {timeRange}
            </Box>
          )}
          <InlineEdit
            value={item.costEstimate > 0 ? formatItemCost(item.costEstimate) : ''}
            placeholder="비용 추가"
            ariaLabel={`${labelBase} 예상 비용 수정`}
            icon="wallet"
            numeric
            disabled={locked}
            onCommit={(next) => {
              const digits = next.replace(/[^\d]/g, '');
              onPatch({ costEstimate: digits ? Number(digits) : 0 });
            }}
          />
          <InlineEdit
            value={item.note ?? ''}
            placeholder="메모 추가"
            ariaLabel={`${labelBase} 메모 수정`}
            disabled={locked}
            onCommit={(next) => onPatch({ note: next || null })}
          />
        </Box>
      </Box>

      <Stack
        direction="row"
        className="ax-row-actions"
        sx={{
          gap: '2px',
          flexShrink: 0,
          opacity: { xs: 1, md: locked ? 1 : 0.45 },
          transition: `opacity ${tokens.motion.fast.duration}ms ${tokens.motion.fast.easing}`,
        }}
      >
        <IconButton
          onClick={onToggleLock}
          aria-label={locked ? `${labelBase} 고정 해제` : `${labelBase} 고정`}
          aria-pressed={locked}
          sx={{
            width: 28,
            height: 28,
            color: locked ? BRAND_TEXT : INK_LOW,
            '&:hover': { bgcolor: PAPER2, color: INK_HI },
          }}
        >
          <LineIcon name={locked ? 'lock' : 'unlock'} size={15} />
        </IconButton>
        <IconButton
          onClick={onDelete}
          disabled={locked}
          aria-label={locked ? `${labelBase} — 고정 해제 후 삭제 가능` : `${labelBase} 삭제`}
          sx={{
            width: 28,
            height: 28,
            color: INK_LOW,
            '&:hover': { bgcolor: PAPER2, color: DANGER },
          }}
        >
          <LineIcon name="trash" size={15} />
        </IconButton>
      </Stack>
    </Box>
  );
}

/**
 * [목적] Day 편집 화면 하단 "직접 추가" 폼 — 유형/이름/예상 비용 입력 후 PlanItem 생성
 * [이유] FlowLine A9: BE 에 POI 검색 API(GET /pois 검색)가 없어 poiId 연결 없이 이름 직접 입력만 지원.
 * [사이드 이팩트] 제출 성공 시 입력값 초기화(호출부가 onSubmit 성공/실패를 처리).
 * [수정 시 고려사항] POI 검색 API 가 추가되면 이 폼에 자동완성 선택지를 추가할 것(follow-up).
 */
function AddItemForm({
  onSubmit,
  isPending,
}: {
  onSubmit: (input: { kind: PlanItemKind; title: string; costEstimate?: number }) => void;
  isPending: boolean;
}): JSX.Element {
  const [kind, setKind] = useState<PlanItemKind>('place');
  const [title, setTitle] = useState('');
  const [cost, setCost] = useState('');

  function handleSubmit(): void {
    const t = title.trim();
    if (!t) return;
    const parsedCost = cost.trim() ? Number(cost) : undefined;
    onSubmit({
      kind,
      title: t,
      costEstimate: parsedCost != null && !Number.isNaN(parsedCost) ? parsedCost : undefined,
    });
    setTitle('');
    setCost('');
  }

  return (
    <Box
      sx={{
        mt: `${tokens.spacing[3]}px`,
        p: `${tokens.spacing[3]}px`,
        bgcolor: PAPER1,
        border: `1px dashed ${LINE}`,
        borderRadius: `${tokens.radius.m}px`,
      }}
    >
      <Typography
        component="h3"
        sx={{
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: '0.1em',
          color: INK_LOW,
          fontFamily: MONO,
          mb: `${tokens.spacing[2]}px`,
        }}
      >
        ADD ITEM · 항목 추가
      </Typography>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={`${tokens.spacing[2]}px`}
        alignItems={{ xs: 'stretch', sm: 'center' }}
      >
        <TextField
          select
          value={kind}
          onChange={(e) => setKind(e.target.value as PlanItemKind)}
          size="small"
          sx={{ minWidth: { sm: 104 }, bgcolor: PAPER0 }}
          inputProps={{ 'aria-label': '항목 유형' }}
        >
          {ITEM_KIND_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSubmit();
          }}
          size="small"
          fullWidth
          placeholder="예: 대포항 회센터 — 입력 후 Enter"
          sx={{ bgcolor: PAPER0 }}
          inputProps={{ 'aria-label': '항목 이름', maxLength: 200 }}
        />
        <TextField
          type="number"
          value={cost}
          onChange={(e) => setCost(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSubmit();
          }}
          size="small"
          placeholder="비용(원)"
          sx={{ minWidth: { sm: 118 }, bgcolor: PAPER0 }}
          inputProps={{ 'aria-label': '예상 비용', min: 0 }}
        />
        <Button
          onClick={handleSubmit}
          variant="contained"
          disableElevation
          disabled={isPending || !title.trim()}
          aria-label="항목 추가"
          sx={{
            flexShrink: 0,
            borderRadius: `${tokens.radius.s}px`,
            fontWeight: 700,
            px: `${tokens.spacing[4]}px`,
          }}
        >
          추가
        </Button>
      </Stack>
    </Box>
  );
}

export function FolioEditPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const [dayIndex, setDayIndex] = useState(0);
  const [liveMessage, setLiveMessage] = useState('');
  const { data: folio, isLoading, isError, error } = useFolio(id);
  const { show } = useToast();
  const reorder = useReorderItems();
  const createDay = useCreateDay();
  const deleteDay = useDeleteDay();
  const createItem = useCreateItem();
  const updateItem = useUpdateItem();
  const deleteItem = useDeleteItem();
  // [수정: 디자인 개편] 편집 화면은 항목마다 즉시 mutate() 하는 자동저장 구조라 "저장하지 않은 변경"이
  // 존재하는 시간은 네트워크 응답을 기다리는 짧은 구간뿐이다 — 이 구간을 isSaving 으로 묶어 상단 바에 노출하고,
  // 이 구간에 탭 닫기/나가기를 시도하면 확인을 띄운다(저장 유실 방지).
  const isSaving =
    reorder.isPending ||
    createDay.isPending ||
    deleteDay.isPending ||
    createItem.isPending ||
    updateItem.isPending ||
    deleteItem.isPending;

  // 브라우저 탭 닫기/새로고침 방지 — 저장 요청이 서버에 도달하기 전에 페이지가 사라지는 것을 막는다.
  useEffect(() => {
    if (!isSaving) return;
    function handleBeforeUnload(e: BeforeUnloadEvent): void {
      e.preventDefault();
      e.returnValue = '';
    }
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isSaving]);

  // 앱 내 라우팅(다른 페이지로 이동) 차단 — 저장 중일 때만 확인창을 띄운다.
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isSaving && currentLocation.pathname !== nextLocation.pathname,
  );
  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    if (window.confirm('저장 중인 변경사항이 있어요. 그래도 나가시겠어요?')) {
      blocker.proceed();
    } else {
      blocker.reset();
    }
  }, [blocker]);
  // FlowLine dcc2c3cc: id 는 라우트 파라미터라 로딩 초기엔 undefined 일 수 있음 — 훅은 항상 호출되어야
  // 하므로(Rules of Hooks) 빈 문자열로 폴백. propose()/apply() 는 folio 로딩 완료 후(아래 early return
  // 통과 후) 렌더되는 UI 에서만 트리거되므로 실질적으로 항상 유효한 id 로 호출된다.
  const refine = useFolioRefine(id ?? '');

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const days = folio?.days ?? [];

  // [목적] Day 추가/삭제로 days.length 가 바뀌면 dayIndex 를 범위 내로 보정
  // [이유] FlowLine A9: 마지막 Day 삭제 시 dayIndex 가 배열 범위를 벗어나 day=undefined 로 화면이 비는 문제 방지
  // [사이드 이팩트] 없음(로컬 state 보정)
  // [수정 시 고려사항] days.length===0(전부 삭제)이면 dayIndex=0 유지 — day undefined 로 "빈 Day" 안내만 노출
  useEffect(() => {
    if (dayIndex >= days.length && days.length > 0) setDayIndex(days.length - 1);
  }, [days.length, dayIndex]);

  if (isLoading) {
    return (
      <PageContainer>
        <Typography>로딩 중…</Typography>
      </PageContainer>
    );
  }

  // FlowLine A9: isError/404 분기 — 기존엔 !folio 도 "로딩 중"으로 오분류되어 무한 로딩처럼 보였음
  if (isError || !folio) {
    const apiErr = error as { status?: number } | undefined;
    const notFound = apiErr?.status === 404;
    return (
      <PageContainer>
        <Typography variant="h2" sx={{ mb: 1 }}>
          {notFound ? '폴리오를 찾을 수 없습니다' : '폴리오를 불러올 수 없습니다'}
        </Typography>
        <Typography color="text.secondary">
          {notFound
            ? '삭제되었거나 접근 권한이 없는 폴리오입니다.'
            : ((error as Error | undefined)?.message ?? '잠시 후 다시 시도해 주세요.')}
        </Typography>
      </PageContainer>
    );
  }

  const day = days[dayIndex];
  // [수정 시 고려사항] folio.id 를 아래 nested function 선언들에서 직접 참조하면 TS 가 클로저 내부에서
  // 좁혀진(non-undefined) 타입을 유지하지 못해 컴파일 에러가 난다 — 좁혀진 시점의 값을 상수로 캡처.
  const folioId = folio.id;

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !day) return;
    const oldIdx = day.items.findIndex((it) => it.id === active.id);
    const newIdx = day.items.findIndex((it) => it.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const reorderedIds = arrayMove(
      day.items.map((it) => it.id),
      oldIdx,
      newIdx,
    );
    setLiveMessage(`항목 ${oldIdx + 1}번을 ${newIdx + 1}번 위치로 이동했습니다.`);
    reorder.mutate({
      folioId: folio.id,
      input: { dayPlanId: day.id, itemIds: reorderedIds },
    });
  };

  function handleAddDay(): void {
    const nextIndex = days.length;
    createDay.mutate(
      { folioId, input: {} },
      {
        onSuccess: () => {
          setDayIndex(nextIndex);
          show('Day를 추가했어요.', 'success');
        },
        onError: () => show('Day 추가에 실패했어요.', 'error'),
      },
    );
  }

  function handleDeleteDay(dId: string): void {
    if (!window.confirm('이 Day를 삭제할까요? 포함된 일정도 함께 삭제됩니다.')) return;
    deleteDay.mutate(
      { folioId, dayId: dId },
      {
        onSuccess: () => show('Day를 삭제했어요.', 'success'),
        onError: () => show('Day 삭제에 실패했어요.', 'error'),
      },
    );
  }

  function handleToggleLock(itemId: string, locked: boolean): void {
    updateItem.mutate(
      { folioId, itemId, input: { locked: !locked } },
      { onError: () => show('고정 상태 변경에 실패했어요.', 'error') },
    );
  }

  /**
   * [목적] 항목 인라인 편집(제목·메모·비용) 커밋 — PATCH /folios/:id/items/:itemId
   * [이유] 2026-09-21 디자인 QA: 편집 화면에서 항목 내용을 고칠 수단이 없어 삭제 후 재추가해야 했다.
   * [사이드 이팩트] useUpdateItem 이 onSettled 에서 folio 캐시 invalidate — 저장 중에는 헤더 칩이 "저장 중…" 으로 바뀜다.
   * [수정 시 고려사항] 금칙어(content.BANNED_WORD_DETECTED) 는 직접 추가와 동일한 문구로 안내한다.
   */
  function handlePatchItem(itemId: string, input: UpdateItemInput): void {
    updateItem.mutate(
      { folioId, itemId, input },
      {
        onError: (err) => {
          const message =
            err instanceof ApiError && err.code === 'content.BANNED_WORD_DETECTED'
              ? '금칙어가 포함되어 있어요. 내용을 수정해 주세요.'
              : '항목 수정에 실패했어요.';
          show(message, 'error');
        },
      },
    );
  }

  function handleDeleteItem(itemId: string): void {
    if (!window.confirm('이 항목을 삭제할까요?')) return;
    deleteItem.mutate(
      { folioId, itemId },
      { onError: () => show('항목 삭제에 실패했어요.', 'error') },
    );
  }

  function handleAddItem(input: {
    kind: PlanItemKind;
    title: string;
    costEstimate?: number;
  }): void {
    if (!day) return;
    createItem.mutate(
      { folioId, input: { dayPlanId: day.id, ...input } },
      {
        onSuccess: () => show('항목을 추가했어요.', 'success'),
        onError: (err) => {
          const message =
            err instanceof ApiError && err.code === 'content.BANNED_WORD_DETECTED'
              ? '금칙어가 포함되어 있어요. 이름을 수정해 주세요.'
              : '항목 추가에 실패했어요.';
          show(message, 'error');
        },
      },
    );
  }

  // [2026-09-21 디자인 QA] 저장 상태 표시 일원화 — 기존엔 상단 칩과 reorder 전용 Alert 3종(실패/저장 중/
  // 자동 저장됨)이 동시에 떠서 같은 사실을 두 군데서 알렸고, Alert 배너가 등장할 때마다 아래 레이아웃이 밀렸다.
  // 상태는 헤더 칩 한 곳에서만 표현한다(저장 중… / 저장 실패 / 모두 저장됨).
  const saveState: 'saving' | 'error' | 'saved' = isSaving
    ? 'saving'
    : reorder.isError
      ? 'error'
      : 'saved';
  const saveLabel =
    saveState === 'saving' ? '저장 중…' : saveState === 'error' ? '저장 실패' : '모두 저장됨';
  const dayItemCount = day?.items.length ?? 0;
  const dayCost = day?.items.reduce((sum, it) => sum + (it.costEstimate || 0), 0) ?? 0;

  return (
    <PageContainer sx={{ maxWidth: 960, py: { xs: 3, md: 4 } }}>
      {/* ── 헤더 ── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: `${tokens.spacing[2]}px`,
          pb: `${tokens.spacing[3]}px`,
          borderBottom: `1px solid ${LINE}`,
          mb: `${tokens.spacing[5]}px`,
        }}
      >
        <Box
          component="span"
          sx={{
            px: '7px',
            py: '3px',
            borderRadius: `${tokens.radius.s}px`,
            bgcolor: PAPER2,
            color: INK_LOW,
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: '0.08em',
            fontFamily: MONO,
          }}
        >
          EDITING
        </Box>
        <Typography
          variant="h2"
          sx={{
            fontSize: { xs: 17, md: 20 },
            fontWeight: 700,
            letterSpacing: '-0.02em',
            color: INK_HI,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {folio.title}
        </Typography>
        <Box sx={{ flex: 1 }} />
        {/* 저장 상태 — 화면 내 유일한 저장 피드백 지점 */}
        <Box
          role="status"
          aria-live="polite"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            px: '10px',
            py: '4px',
            borderRadius: 99,
            fontSize: 11,
            fontWeight: 700,
            fontFamily: MONO,
            bgcolor: PAPER2,
            color: saveState === 'error' ? DANGER : saveState === 'saving' ? BRAND_TEXT : INK_LOW,
          }}
        >
          {saveLabel}
        </Box>
        <ButtonBase
          component={RouterLink}
          to={`/folio/${folio.id}`}
          aria-label="뷰로 돌아가기"
          sx={{
            px: '11px',
            py: '6px',
            borderRadius: `${tokens.radius.s}px`,
            border: `1px solid ${LINE}`,
            fontSize: 12.5,
            fontWeight: 600,
            color: INK_MID,
            '&:hover': { bgcolor: PAPER2, color: INK_HI },
            '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
          }}
        >
          ← 뷰로 돌아가기
        </ButtonBase>
      </Box>

      {/* 스크린리더 라이브 영역 */}
      <Box role="status" aria-live="polite" sx={{ position: 'absolute', left: -10000 }}>
        {liveMessage}
      </Box>

      {/* ── Day 탭 ── */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={`${tokens.spacing[2]}px`}
        sx={{ flexWrap: 'wrap', rowGap: `${tokens.spacing[2]}px` }}
      >
        <Tabs
          value={days.length > 0 ? dayIndex : false}
          onChange={(_, v: number) => setDayIndex(v)}
          aria-label="Day 선택"
          variant="scrollable"
          scrollButtons={false}
          sx={{
            minHeight: 0,
            '& .MuiTabs-indicator': { display: 'none' },
            '& .MuiTabs-flexContainer': { gap: '6px' },
          }}
        >
          {days.map((d, i) => (
            <Tab
              key={d.id}
              label={`Day ${d.dayIndex}`}
              id={`edit-tab-${i}`}
              sx={{
                minHeight: 0,
                minWidth: 0,
                px: '14px',
                py: '7px',
                borderRadius: 99,
                border: `1px solid ${LINE}`,
                fontSize: 13,
                fontWeight: 600,
                color: INK_MID,
                textTransform: 'none',
                '&.Mui-selected': { bgcolor: INK_HI, color: PAPER0, borderColor: INK_HI },
              }}
            />
          ))}
        </Tabs>
        <Button
          onClick={handleAddDay}
          disabled={createDay.isPending}
          aria-label="Day 추가"
          size="small"
          startIcon={<LineIcon name="plus" size={13} />}
          sx={{
            borderRadius: 99,
            border: `1px dashed ${BRAND}`,
            color: BRAND_TEXT,
            fontSize: 12.5,
            fontWeight: 600,
            px: '13px',
            textTransform: 'none',
          }}
        >
          Day 추가
        </Button>
      </Stack>

      {days.length === 0 && (
        <Box
          sx={{
            mt: `${tokens.spacing[5]}px`,
            p: `${tokens.spacing[8]}px ${tokens.spacing[5]}px`,
            textAlign: 'center',
            border: `1px dashed ${LINE}`,
            borderRadius: `${tokens.radius.l}px`,
            bgcolor: PAPER1,
          }}
        >
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: INK_HI }}>
            아직 Day가 없어요
          </Typography>
          <Typography sx={{ fontSize: 13, color: INK_MID, mt: '6px', lineHeight: 1.6 }}>
            Day를 만들면 그 안에 장소·식사·메모를 차례대로 넣을 수 있어요.
          </Typography>
          <Button
            onClick={handleAddDay}
            disabled={createDay.isPending}
            variant="contained"
            disableElevation
            aria-label="첫 Day 만들기"
            sx={{
              mt: `${tokens.spacing[4]}px`,
              borderRadius: `${tokens.radius.s}px`,
              fontWeight: 700,
              textTransform: 'none',
            }}
          >
            첫 Day 만들기
          </Button>
        </Box>
      )}

      {day && (
        <Stack spacing={`${tokens.spacing[3]}px`} sx={{ mt: `${tokens.spacing[6]}px` }}>
          <Stack
            direction="row"
            alignItems="center"
            sx={{ pb: `${tokens.spacing[2]}px`, borderBottom: `1px solid ${LINE}` }}
          >
            <Typography variant="subtitle1" sx={{ fontSize: 15, fontWeight: 700, color: INK_HI }}>
              Day {day.dayIndex}
            </Typography>
            {/* Day 요약 — 항목 수·예상 비용을 한눈에 */}
            <Typography sx={{ ml: '10px', fontSize: 11.5, fontFamily: MONO, color: INK_LOW }}>
              항목 {dayItemCount}개{dayCost > 0 ? ` · ${formatItemCost(dayCost)}` : ''}
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Button
              size="small"
              onClick={() => handleDeleteDay(day.id)}
              aria-label={`Day ${day.dayIndex} 삭제`}
              sx={{
                fontSize: 12,
                fontWeight: 600,
                color: INK_LOW,
                textTransform: 'none',
                '&:hover': { color: DANGER, bgcolor: PAPER2 },
              }}
            >
              Day 삭제
            </Button>
          </Stack>

          {day.items.length === 0 ? (
            <Box
              sx={{
                p: `${tokens.spacing[8]}px ${tokens.spacing[5]}px`,
                textAlign: 'center',
                border: `1px dashed ${LINE}`,
                borderRadius: `${tokens.radius.m}px`,
                bgcolor: PAPER1,
              }}
            >
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: INK_HI }}>
                이 Day는 아직 비어 있어요
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: INK_MID, mt: '5px', lineHeight: 1.6 }}>
                아래 “항목 추가”에서 장소·식사·메모를 넣거나, AI 보정으로 한 번에 채울 수 있어요.
              </Typography>
            </Box>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={day.items.map((it) => it.id)}
                strategy={verticalListSortingStrategy}
              >
                <Stack spacing={`${tokens.spacing[2]}px`}>
                  {day.items.map((it) => (
                    <SortableRow
                      key={it.id}
                      item={it}
                      onToggleLock={() => handleToggleLock(it.id, it.locked)}
                      onDelete={() => handleDeleteItem(it.id)}
                      onPatch={(input) => handlePatchItem(it.id, input)}
                    />
                  ))}
                </Stack>
              </SortableContext>
            </DndContext>
          )}

          <AddItemForm onSubmit={handleAddItem} isPending={createItem.isPending} />

          {/* FlowLine dcc2c3cc: AI 자연어 보정 — 활성 Day(day.dayIndex) 기준 제안 요청
              [2026-09-21 디자인 QA] 빠른 보정 칩들이 맥락 없이 떠 있어 구분선+라벨을 앞에 둔다. */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: `${tokens.spacing[2]}px`,
              mt: `${tokens.spacing[4]}px`,
            }}
          >
            <Typography
              component="h3"
              sx={{
                fontSize: 10.5,
                fontWeight: 700,
                letterSpacing: '0.1em',
                color: INK_LOW,
                fontFamily: MONO,
              }}
            >
              AI 보정
            </Typography>
            <Box sx={{ flex: 1, height: '1px', bgcolor: LINE }} />
          </Box>
          <RefineInputBar
            key={refine.resetKey}
            onSubmit={(instruction) =>
              refine.propose(instruction, { type: 'day', dayIndex: day.dayIndex })
            }
            isSubmitting={refine.isProposing}
          />
        </Stack>
      )}

      <RefineProposalDialog
        open={refine.open}
        proposal={refine.proposal}
        isProposing={refine.isProposing}
        proposeErrorMessage={refine.proposeErrorMessage}
        isApplying={refine.isApplying}
        applyErrorMessage={refine.applyErrorMessage}
        onApply={refine.apply}
        onCancel={refine.cancel}
      />
    </PageContainer>
  );
}
